import {
  CheckCircleFillIcon,
  CheckCircleOutlineIcon,
  ChevronRightCircleFillIcon,
  ChevronRightCircleOutlineIcon,
  CircleFillIcon,
  CircleOutlineIcon,
  CircleRingFillIcon,
  CircleRingOutlineIcon,
  ErrorFillIcon,
  ErrorOutlineIcon,
  SvgIconProps,
  NoEntryFillIcon,
  NoEntryOutlineIcon,
} from '@applyboard/ui-icons';
import React from 'react';
import { Flex, FlexProps } from '~/components';
import {
  Intent,
  IntentProps,
  Responsive,
  ResponsiveHidingProps,
  SpacingToken,
  useFullTheme,
} from '~/design';
import { IconWrapper } from '~/internal';
import {
  findChildOfType,
  findChildrenOfTypeRecursively,
  isChildType,
  useResponsive,
} from '~/utils';
import { useResponsiveHideStyle } from '~/utils/styling';
import { useContextOrThrow } from '~/utils/useContextOrThrow';

interface TimelineContextProps {
  align: TimelineProps['align'];
  numItems: number;
  orientation: TimelineProps['orientation'];
  position: TimelineProps['position'];
  rowGap: Required<TimelineProps>['rowGap'];
  columnGap: Required<TimelineProps>['columnGap'];
  value: TimelineProps['value'];
}

const TimelineContext = React.createContext<TimelineContextProps | null>(null);

type Gap = Exclude<SpacingToken, '1px' | '2px'>;

export interface TimelineProps extends ResponsiveHidingProps {
  /** The alignment of the timeline. (default: center) */
  align?: 'left' | 'center' | 'right';

  /** The aria-label of the timeline. */
  'aria-label'?: string;

  /** The aria-labelledby of the timeline. Use this if an element outside of the timeline sufficiently
   * describes the timeline. */
  'aria-labelledby'?: string;

  /** The content of the timeline. */
  children?: React.ReactNode;

  /** The amount of space between the timeline's dots and its neighbouring content. */
  columnGap?: Gap | Responsive<Gap>;

  /** The orientation of the timeline. (default: vertical) */
  orientation?: 'horizontal' | 'vertical';

  /** The position of the elements on the timeline. (default: right) */
  position?: 'left' | 'alternate' | 'right';

  /** The amount of space between each timeline item. */
  rowGap?: Gap | Responsive<Gap>;

  /** The currently selected step. */
  value?: number;
}

/**
 * Timeline is a component that renders a list of items that correspond to a sequence of events over time.
 */
export function Timeline({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  align: _align,
  children,
  columnGap = 12,
  hideAbove,
  hideBelow,
  orientation = 'vertical',
  position: _position,
  rowGap = 12,
  value,
}: TimelineProps) {
  if (!!ariaLabel && !!ariaLabelledby) {
    throw new Error('Only one of aria-label, aria-labelledby can be supplied for Timeline');
  } else if (!ariaLabel && !ariaLabelledby) {
    throw new Error('Provide either an aria-label or aria-labelledby for Timeline');
  }

  if (orientation === 'horizontal') {
    if (_align || _position) {
      throw new Error('Horizontal Timelines do not support "align" or "position" at this time');
    }
  }

  const hideStyle = useResponsiveHideStyle({ hideBelow, hideAbove });

  const align = _align ?? 'left';
  const position = _position ?? 'right';
  const theme = useFullTheme();
  const responsive = useResponsive();
  const childrenArray = findChildrenOfTypeRecursively(children, [Timeline.Item]);

  const hasOppositeContent = childrenArray.find(child => {
    const grandchildren = (child as unknown as React.ReactElement<TimelineItemProps>).props
      .children;

    return !!(
      (React.Children.toArray(grandchildren ?? []) ?? []) as unknown as React.ReactChild[]
    ).find(grandchild => isChildType(grandchild, TimelineItem.Opposite));
  });

  let display, gridTemplateColumns;
  if (orientation === 'vertical') {
    display = 'grid';
    if (align === 'left') {
      gridTemplateColumns = 'auto auto 1fr';
    } else if (align === 'right') {
      gridTemplateColumns = '1fr auto auto';
    } else {
      gridTemplateColumns = '1fr auto 1fr';
    }
  } else {
    display = 'flex';
  }

  return (
    <TimelineContext.Provider
      value={{
        align,
        numItems: React.Children.count(childrenArray),
        orientation,
        position,
        rowGap,
        columnGap,
        value,
      }}
    >
      <ol
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        css={[
          hideStyle,
          {
            display,
            gridTemplateColumns,
            marginBlock: 0,
            paddingInline: 0,
          },
          responsive.css({
            columnGap: responsive.map(columnGap, prop => prop && theme.spacing[prop]),
            marginLeft: responsive.map(columnGap, prop =>
              !prop
                ? undefined
                : !hasOppositeContent && align === 'left'
                  ? `-${theme.spacing[prop]}`
                  : 0,
            ),
          }),
        ]}
      >
        {childrenArray.map((child: unknown, index) => (
          <InnerTimelineItem
            index={index}
            intent={(child as React.ReactElement<TimelineItemProps>).props.intent}
            key={
              (child as React.ReactElement<TimelineItemProps>).key ||
              `__cds__timeline_item-${index}`
            }
            nextItem={childrenArray[index + 1]}
            prevItem={childrenArray[index - 1]}
            reverse={position === 'alternate' && index % 2 === 1}
            pb={(child as React.ReactElement<TimelineItemProps>).props.pb}
          >
            {child as React.ReactElement<TimelineItemProps>}
          </InnerTimelineItem>
        ))}
      </ol>
    </TimelineContext.Provider>
  );
}

interface InnerTimelineItemProps extends IntentProps {
  children: React.ReactNode;
  index?: number;
  nextItem?: React.ReactNode;
  prevItem?: React.ReactNode;
  reverse?: boolean;
  pb?: FlexProps['pb'];
}

function InnerTimelineItem({
  children: timelineItemChild,
  index,
  intent,
  nextItem,
  prevItem,
  reverse = false,
  pb,
}: InnerTimelineItemProps) {
  const { orientation, position, value, rowGap, numItems } = useContextOrThrow(
    TimelineContext,
    'Timeline.Item must be used within Timeline',
  );

  const meaningfulChildren = (timelineItemChild as unknown as React.ReactElement<TimelineItemProps>)
    .props.children;

  const dotChild = findChildOfType(
    meaningfulChildren,
    TimelineItem.Dot,
  ) as unknown as React.ReactElement<TimelineItemDotProps>;

  const oppositeContentChild = findChildOfType(meaningfulChildren, TimelineItem.Opposite);

  const remainingChildren = (
    <div css={{ width: '100%' }}>
      {React.Children.toArray(meaningfulChildren).filter(
        child =>
          !isChildType(child, TimelineItem.Dot) && !isChildType(child, TimelineItem.Opposite),
      )}
    </div>
  );

  let leftContent = null,
    rightContent = null;
  if (!reverse) {
    leftContent = position === 'left' ? remainingChildren : oppositeContentChild;
    rightContent = position === 'left' ? oppositeContentChild : remainingChildren;
  } else {
    leftContent = position === 'left' ? oppositeContentChild : remainingChildren;
    rightContent = position === 'left' ? remainingChildren : oppositeContentChild;
  }

  return (
    <li
      aria-current={value === index ? 'step' : undefined}
      css={[
        {
          display: 'contents',
        },
        orientation === 'horizontal' && {
          display: 'flex',
          flex: '1 1 0',
          flexDirection: 'column',
        },
      ]}
    >
      <Flex
        align="center"
        justify={orientation === 'vertical' ? 'end' : 'center'}
        pb={pb || (orientation === 'horizontal' || index === numItems - 1 ? 0 : rowGap)}
      >
        {leftContent}
      </Flex>
      <InnerTimelineDot
        {...(dotChild?.props ?? {})}
        index={index}
        firstLineIntent={
          (prevItem as unknown as React.ReactElement<TimelineItemProps>)?.props.intent
        }
        secondLineIntent={intent}
        nextIntent={
          (
            findChildOfType(
              (nextItem as unknown as React.ReactElement<TimelineItemProps>)?.props.children,
              TimelineItem.Dot,
            ) as unknown as React.ReactElement<TimelineItemDotProps>
          )?.props.intent
        }
      />
      <Flex
        align="center"
        pb={pb || (orientation === 'horizontal' || index === numItems - 1 ? 0 : rowGap)}
      >
        {rightContent}
      </Flex>
    </li>
  );
}

export interface TimelineItemProps extends IntentProps {
  /** The content of this entry in the timeline. */
  children: React.ReactNode;

  pb?: FlexProps['pb'];
}

function TimelineItem({ children }: TimelineItemProps) {
  return <InnerTimelineItem>{children}</InnerTimelineItem>;
}

Timeline.Item = TimelineItem;

interface InnerTimelineDotProps extends TimelineItemDotProps {
  index?: number;
  firstLineIntent?: Intent;
  secondLineIntent?: Intent;
  nextIntent?: Intent;
}

function InnerTimelineDot({
  align: alignSelf = 'center',
  icon,
  intent = 'secondary',
  index,
  firstLineIntent,
  secondLineIntent,
  nextIntent = 'secondary',
  size = 'md',
  variant = 'fill',
}: InnerTimelineDotProps) {
  const { colors, spacing } = useFullTheme();
  const responsive = useResponsive();
  const { numItems, orientation, columnGap } = useContextOrThrow(
    TimelineContext,
    'Timeline.Item.Dot must be used within Timeline.Item',
  );

  const [IconFill, IconOutline] =
    icon === 'check'
      ? [CheckCircleFillIcon, CheckCircleOutlineIcon]
      : icon === 'chevron'
        ? [ChevronRightCircleFillIcon, ChevronRightCircleOutlineIcon]
        : icon === 'circle'
          ? [CircleFillIcon, CircleOutlineIcon]
          : icon === 'error'
            ? [ErrorFillIcon, ErrorOutlineIcon]
            : icon === 'no-entry'
              ? [NoEntryFillIcon, NoEntryOutlineIcon]
              : [CircleRingFillIcon, CircleRingOutlineIcon];
  const Icon = variant === 'fill' ? IconFill : IconOutline;

  const iconSizePx = {
    xs: '10px',
    sm: '16px',
    md: '24px',
    lg: '36px',
    xl: '48px',
  }[size];

  const iconOffset = {
    xs: '7px',
    sm: '0px',
    md: '0px',
    lg: '0px',
    xl: '0px',
  }[size];

  const lineColor = {
    magic: colors.actionMagic,
    negative: colors.actionNegative,
    positive: colors.actionPositive,
    primary: colors.actionPrimary,
    secondary: colors.bgSecondaryHighlightPressed,
    warning: colors.actionWarning,
  };

  return (
    <div
      css={{
        alignItems: 'center',
        alignSelf: orientation === 'horizontal' ? 'center' : alignSelf,
        display: 'flex',
        height: orientation === 'vertical' ? '100%' : undefined,
        justifyContent: 'center',
        position: 'relative',
        width: orientation === 'horizontal' ? '100%' : undefined,
      }}
      data-testid={`timeline-dot-${index}`}
    >
      {index !== 0 && (
        <div
          data-testid="topline"
          css={[
            {
              border: `2px solid ${lineColor[firstLineIntent ?? intent]}`,
              position: 'absolute',
            },
            orientation === 'vertical' && [
              alignSelf === 'start' && {
                height: iconOffset,
                top: 0,
                display: iconOffset === '0px' ? 'none' : undefined,
              },
              alignSelf === 'center' &&
                responsive.css({
                  height: `calc(50% - (${iconSizePx} / 2))`,
                  top: 0,
                }),
            ],
            orientation === 'horizontal' && {
              left: 0,
              width: `calc(50% - ${iconSizePx} / 2)`,
            },
          ]}
        />
      )}

      <div
        css={{
          alignSelf: orientation === 'horizontal' ? 'center' : alignSelf,
          lineHeight: 0,
          position: 'relative',
          top: iconOffset,
        }}
      >
        <IconWrapper
          allowInteractions={false}
          contrast={intent === 'secondary' ? 'low' : 'mid'}
          intent={intent}
          selector="div"
        >
          <Icon size={size} />
        </IconWrapper>
      </div>

      {index !== numItems - 1 && (
        <div
          data-testid="bottomline"
          css={[
            {
              border: `2px solid ${lineColor[secondLineIntent ?? nextIntent]}`,
              position: 'absolute',
            },
            orientation === 'vertical' && [
              alignSelf === 'start' && {
                height: `calc(100% - ${iconSizePx} - ${iconOffset})`,
                top: `calc(${iconSizePx} + ${iconOffset})`,
              },
              alignSelf === 'center' && {
                height: `calc(50% - (${iconSizePx} / 2))`,
                top: `calc(50% + (${iconSizePx} / 2))`,
              },
            ],
            orientation === 'horizontal' &&
              responsive.css({
                width: responsive.map(
                  columnGap,
                  prop => `calc(50% - (${iconSizePx} / 2) + ${spacing[prop]})`,
                ),
                left: `calc(50% + (${iconSizePx} / 2))`,
              }),
          ]}
        />
      )}
    </div>
  );
}

export interface TimelineItemDotProps extends IntentProps {
  /** The alignment of the dot with the main content. */
  align?: 'start' | 'center';

  /** The icon. (default: rings) */
  icon?: 'check' | 'circle' | 'chevron' | 'error' | 'rings' | 'no-entry';

  /** The size of the dot. */
  size?: SvgIconProps['size'];

  /** The variant of the icon. */
  variant?: 'fill' | 'outline';
}

TimelineItem.Dot = function TimelineItemDot({
  align = 'center',
  icon = 'rings',
  intent = 'secondary',
  size = 'md',
  variant = 'fill',
}: TimelineItemDotProps) {
  return (
    <InnerTimelineDot align={align} icon={icon} intent={intent} size={size} variant={variant} />
  );
};

export interface TimelineItemOppositeProps {
  children: React.ReactNode;
}

TimelineItem.Opposite = function TimelineItemOpposite({ children }: TimelineItemOppositeProps) {
  if (!React.useContext(TimelineContext)) {
    // TODO: should we use a separate context for Timeline.Item?
    throw new Error('Timeline.Item.Opposite must be used within Timeline.Item');
  }
  return <div>{children}</div>;
};
